import React, { FC, useRef, useState } from 'react';
import { Box, Tooltip, useTheme } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import strings from '../../../common/Translation/Translate';

interface BookingMonthYearPickerProps {
  value: Date | string | null | undefined;
  locale: string;
  onChange: (value: Dayjs) => void;
  selectDay?: boolean;
}

type PickerView = 'year' | 'month' | 'day';

const BookingMonthYearPicker: FC<BookingMonthYearPickerProps> = ({ value, locale, onChange, selectDay = false }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const finalView: PickerView = selectDay ? 'day' : 'month';
  const viewRef = useRef<PickerView>(finalView);
  const theme = useTheme();
  const monthValue = value ? dayjs(value).locale(locale) : dayjs();
  const label = selectDay ? strings.booking_diary_select_date : strings.reports_demand_calendar_select_month;
  const displayFormat = selectDay ? 'D MMM YYYY' : 'MMM YYYY';

  const openPicker = () => {
    viewRef.current = finalView;
    setOpen(true);
  };

  return (
    <LocalizationProvider adapterLocale={locale} dateAdapter={AdapterDayjs}>
      <Tooltip title={label} placement="bottom">
        <span>
          <DesktopDatePicker
            open={open}
            onOpen={openPicker}
            onClose={() => setOpen(false)}
            views={selectDay ? ['year', 'month', 'day'] : ['year', 'month']}
            openTo={finalView}
            onViewChange={nextView => {
              viewRef.current = nextView;
            }}
            inputFormat={displayFormat}
            value={monthValue}
            onChange={(newValue: Dayjs | null) => {
              if (!newValue || viewRef.current !== finalView) {
                return;
              }
              onChange(newValue);
              setOpen(false);
            }}
            PopperProps={{
              anchorEl: anchorRef.current,
              placement: 'bottom',
              sx: { zIndex: theme.zIndex.modal + 2 },
            }}
            renderInput={params => (
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <input
                  ref={params.inputRef}
                  {...params.inputProps}
                  tabIndex={-1}
                  readOnly
                  aria-hidden
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    pointerEvents: 'none',
                    width: 1,
                    height: 1,
                    border: 0,
                    padding: 0,
                    margin: 0,
                  }}
                />
                <Box
                  ref={anchorRef}
                  component="button"
                  type="button"
                  onClick={openPicker}
                  aria-label={label}
                  sx={{
                    border: 0,
                    background: 'none',
                    cursor: 'pointer',
                    px: 0.5,
                    py: 0.25,
                    fontFamily: 'inherit',
                    fontWeight: 700,
                    fontSize: '0.9375rem',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    color: 'text.primary',
                    '&:hover': { color: 'primary.main' },
                  }}>
                  {monthValue.format(displayFormat)}
                </Box>
              </Box>
            )}
          />
        </span>
      </Tooltip>
    </LocalizationProvider>
  );
};

export default BookingMonthYearPicker;
